// src/services/reminderService.ts
// Domain-specific service for reminder operations

import { apiClient } from "./api";
import { RequestParams } from "./api";
import { API_ENDPOINTS } from "../constants/index";

// Type definitions

/** A reminder from /reminders: `ReminderRead`, plus the fields it may carry. */
export interface Reminder {
  id: number;
  note_id?: number;
  reminder_time: string;
  owner_id?: number;
  owner_email?: string;
  title?: string;
  description?: string;
  completed?: boolean;
  completed_at?: string;
  snoozed?: boolean;
  created_at?: string;
  updated_at?: string;
  priority?: "low" | "medium" | "high";
}

export interface CreateReminderData {
  title?: string;
  description?: string;
  reminder_time: string | Date;
  note_id?: number;
  priority?: "low" | "medium" | "high";
}

export interface UpdateReminderData {
  title?: string;
  description?: string;
  reminder_time?: string | Date;
  note_id?: number;
  priority?: "low" | "medium" | "high";
  completed?: boolean;
  snoozed?: boolean;
}

/**
 * Service for reminder operations
 */
export class ReminderService {
  /**
   * Get all reminders for the user
   */
  async getReminders(): Promise<Reminder[]> {
    return await apiClient.get<Reminder[]>("/reminders");
  }

  /**
   * Get reminders for a specific note
   */
  async getRemindersByNote(noteId: number): Promise<Reminder[]> {
    return await apiClient.get<Reminder[]>(`/notes/${noteId}/reminders`);
  }

  /**
   * Create a new reminder
   */
  async createReminder(
    reminderData: CreateReminderData,
    userEmail: string,
  ): Promise<Reminder> {
    const processedData = {
      ...reminderData,
      owner_email: userEmail,
      reminder_time: new Date(reminderData.reminder_time).toISOString(),
    };

    const newReminder = await apiClient.post<Reminder>(
      API_ENDPOINTS.REMINDERS,
      processedData,
    );

    return newReminder;
  }

  /**
   * Update an existing reminder
   */
  async updateReminder(
    reminderId: number,
    reminderData: UpdateReminderData,
  ): Promise<Reminder> {
    const processedData = {
      ...reminderData,
      reminder_time: reminderData.reminder_time
        ? new Date(reminderData.reminder_time).toISOString()
        : undefined,
    };

    return await apiClient.patch<Reminder>(
      `/reminders/${reminderId}`,
      processedData,
    );
  }

  /**
   * Delete a reminder
   */
  async deleteReminder(reminderId: number): Promise<number> {
    await apiClient.delete(`/reminders/${reminderId}`);
    return reminderId;
  }

  /**
   * Mark reminder as completed
   */
  async markCompleted(reminderId: number): Promise<Reminder> {
    const completedReminder = await apiClient.patch<Reminder>(
      `/reminders/${reminderId}`,
      {
        completed: true,
        completed_at: new Date().toISOString(),
      },
    );

    return completedReminder;
  }

  /**
   * Complete a reminder (alias for markCompleted)
   */
  async completeReminder(reminderId: number): Promise<Reminder> {
    return await this.markCompleted(reminderId);
  }

  /**
   * Snooze a reminder
   */
  async snoozeReminder(
    reminderId: number,
    snoozeUntil: string | Date,
  ): Promise<Reminder> {
    return await apiClient.patch<Reminder>(`/reminders/${reminderId}`, {
      reminder_time: new Date(snoozeUntil).toISOString(),
      snoozed: true,
    });
  }

  /**
   * Get upcoming reminders (due within specified time)
   */
  async getUpcomingReminders(withinHours: number = 24): Promise<Reminder[]> {
    const until = new Date();
    until.setHours(until.getHours() + withinHours);

    const params: RequestParams = {
      due_before: until.toISOString(),
      completed: false,
    };

    return await apiClient.get<Reminder[]>("/reminders", params);
  }
}

// Create singleton instance
export const reminderService = new ReminderService();
